import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@fluentui/react-components';
import { AccessMessage } from '../components/AccessMessage';
import { SupportEmail } from '../components/SupportEmail';
import { buildPageAccessRequestSubject } from '../constants/support';
import InsufficientPermissions from '../components/icons/InsufficientPermissions';

export interface NotEntitledSuggestion {
  path: string;
  label: string;
}

interface NotEntitledProps {
  /** Human readable area the blocked page belongs to, e.g. "Marketing operations". */
  area?: string;
  /** Page the user may open instead. When omitted the CTA is hidden. */
  suggested?: NotEntitledSuggestion;
}

/**
 * Entitlement experience shown to an authenticated user who holds app roles but
 * not the ones the requested page requires. Rendered in place by RoleGuard so
 * the attempted URL stays in the address bar, and inside the app shell so the
 * header and navigation remain available.
 */
export const NotEntitled: React.FC<NotEntitledProps> = ({ area, suggested }) => {
  const navigate = useNavigate();

  const body = area
    ? `The page you are trying to access is part of ${area} and requires additional permissions to access.`
    : 'The page you are trying to access requires additional permissions to access.';

  return (
    <AccessMessage
      icon={<InsufficientPermissions />}
      title="You're not entitled to this page"
      body={body}
      supporting={
        <>
          {suggested
            ? `You have access to use the ${suggested.label} page located in the left navigation bar. `
            : ''}
          <br/>To request access to this page, email{' '}
          <SupportEmail subject={buildPageAccessRequestSubject(area)} />
        </>
      }
      actions={
        suggested && (
          <Button
            appearance="primary"
            onClick={() => navigate(suggested.path)}
          >
            {`Go to ${suggested.label}`}
          </Button>
        )
      }
    />
  );
};
